import { Button, Icon, lightTheme } from '@arcadiapower/shrike';
import { useState, useLayoutEffect } from 'react';
import { ImageSelector, GET_STARTED, GET_STARTED_CAROUSEL } from '~/components/image';
import { Title, StyledUl, UlListItem, Text } from '~/components/typography';
import { useCopy } from '~/hooks';
import {
  ImageWrapper,
  HeaderSection,
  ImageFlexBox,
  StyledDialogModal,
  StyledDialogDesktopModal as Dialog,
  StyledDesktopContainer,
  StyledMobileContainer,
  TextButtonSection,
} from './card.component.responsive.style';

export const Card = ({ context, overrideCopy, next, skip, done }: CardProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const [showMobile, setShowMobile] = useState(false);

  useLayoutEffect(() => {
    const mql = window.matchMedia(`(max-width: ${lightTheme.breakpoints.tabletPortrait}px)`);
    setShowMobile(mql.matches);

    const onChange = (event: MediaQueryListEvent) => setShowMobile(event.matches);

    mql.addEventListener('change', onChange);

    return () => mql.removeEventListener('change', onChange);
  }, []);

  if (showMobile)
    return (
      <StyledDialogModal aria-label="micro-learning-mobile-dialog" size="full-screen">
        <StyledMobileContainer>
          <CardComponent copy={copy} next={next} skip={skip} done={done} />
        </StyledMobileContainer>
      </StyledDialogModal>
    );

  return (
    <Dialog aria-label="micro-learning-dialog">
      <StyledDesktopContainer>
        <CardComponent copy={copy} next={next} skip={skip} done={done} />
      </StyledDesktopContainer>
    </Dialog>
  );
};

const CardComponent = ({ copy, next, skip, done }) => {
  const { header, carouselIndex, imageType, copyStrings } = copy;
  return (
    <>
      <HeaderSection>
        <ImageWrapper>
          <ImageSelector type={carouselIndex} />
        </ImageWrapper>
        {skip && (
          <Button
            mode="default"
            fullWidth={false}
            size="small"
            type="button"
            onClick={skip}
            style={{ position: 'absolute' }}
            tabIndex={-1}
          >
            Skip
          </Button>
        )}
      </HeaderSection>
      <ImageFlexBox>
        <ImageSelector type={imageType} maxWidth="300px" />
      </ImageFlexBox>
      <TextButtonSection>
        <Title
          fontFamily="DM Sans, sans-serif"
          textAlign="center"
          fontSize="28px"
          fontWeight={700}
          margin="0 0 16px "
        >
          {header}
        </Title>
        {copyStrings.length > 1 ? (
          <StyledUl>
            {copyStrings.map(item => (
              <UlListItem style={{ color: '#577671', gap: '16px', textAlign: 'left' }} key={item}>
                <Icon scale={2} icon="Node" color="accent9" style={{ marginTop: '4px' }} />
                {item}
              </UlListItem>
            ))}
          </StyledUl>
        ) : (
          <Text color="secondary" textAlign="center">
            {copyStrings[0]}
          </Text>
        )}
        {!done && next && carouselIndex === 'GET_STARTED_CAROUSEL' ? (
          <Button mode="primary" fullWidth margin={{ top: '24px' }} size="medium" onClick={next}>
            Let's do it
          </Button>
        ) : (
          !done &&
          next && (
            <Button mode="primary" fullWidth margin={{ top: '24px' }} size="medium" onClick={next}>
              Next
            </Button>
          )
        )}
        {done && (
          <Button mode="primary" fullWidth margin={{ top: '24px' }} size="medium" onClick={done}>
            Sign up
          </Button>
        )}
      </TextButtonSection>
    </>
  );
};

export const defaultCopy = () => ({
  header: 'Good news! Community Solar is available to you',
  copyStrings: [
    'Here’s everything you need to know about Community Solar at Arcadia. It takes 2 minutes to learn how it works and get started.',
  ],
  imageType: GET_STARTED,
  carouselIndex: GET_STARTED_CAROUSEL,
});

export type CardCopy = ReturnType<typeof defaultCopy>;

export interface CardProps {
  context: CardContext;
  overrideCopy?: (context: CardContext) => Partial<CardCopy>;
  next?: () => void;
  skip?: () => void;
  done?: () => void;
}

export interface CardContext {}
